import React, { Component } from "react";
import { can } from "../../utils/permission";
import { loading, alert, NavbarAdmin, Modal, GET, POST, PUT, format_date, t } from "../../components/CustomComponent.js";
import "../../assets/css/warranty-expiry.css";

const THRESHOLDS = [90, 60, 30, 7];
const WEEK_DAYS = [1, 2, 3, 4, 5];
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = ["00", "15", "30", "45"];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const MAX_SUGGEST = 8;
const STATUS_COLORS = { on: "#1F8A4C", off: "#6B7280", no_recipient: "#D64545" };

const fill = (text, values) => Object.keys(values).reduce((acc, k) => acc.replace(`{${k}}`, values[k]), text);

const lang = () => (localStorage.getItem("lang") || "TH").toLowerCase();
const fmtDateTime = (value) => (value ? format_date(value, "dd/mm/yyyy hh:mm", lang()) : "-");

// ฟอร์มที่แก้ได้ (สำเนาจากข้อมูลที่ backend ส่งมา)
const toForm = (s) => ({
  is_enabled: !!s.is_enabled,
  buckets: [...(s.buckets || [])],
  frequency: s.frequency || "daily",
  weekly_day: s.weekly_day || 1,
  send_time: s.send_time || "08:00",
  urgent_immediate: !!s.urgent_immediate,
  include_owner_sales: !!s.include_owner_sales,
  attach_excel: !!s.attach_excel,
  recipients: (s.recipients || []).map((r) => ({
    type: r.type,
    kind: r.kind,
    user_uid: r.user_uid || null,
    name: r.name || "",
    email: r.email || "",
    is_active: r.is_active !== false,
  })),
});

// สถานะที่คำนวณจากฟอร์มปัจจุบัน (ให้ป้ายเปลี่ยนทันทีที่แก้) - ตรงกับ toResponse ฝั่ง backend
const formStatus = (form) => {
  if (!form.is_enabled) return "off";
  return form.recipients.some((r) => r.type === "to" && r.is_active) ? "on" : "no_recipient";
};

// หน้า "ตั้งค่าแจ้งเตือนใกล้หมดประกัน" (ต่อบริษัท) - ยกจาก SNAPA warranty-alert-settings.js (ตัด BU / ไส้กรอง / PM)
//   GET  admin/warranty-alert-settings               -> การตั้งค่าของบริษัท
//   PUT  admin/warranty-alert-settings               -> บันทึก (recipients: {type, user_uid | email})
//   POST admin/warranty-alert-settings/test-send     -> {sent_to,row_count,new_count}
//   GET  admin/warranty-alert-settings/preview-email -> {subject,html,row_count,new_count} (ไม่ส่ง ไม่ log)
export default class AdminWarrantyAlertSettings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      item: null,
      loaded: false,
      preview: null,
      form: null,
      base_json: "",
      users: [],
      input: { to: "", cc: "" },
      input_error: { to: "", cc: "" },
      focus_list: "",
      saving: false,
    };
  }

  async componentDidMount() {
    loading(false);
    const users = await GET("admin/users", { page_size: 10000, page: 0 });
    if (users?.status && Array.isArray(users.data)) this.setState({ users: users.data });
    await this.Load();
  }

  Load = async () => {
    loading(true);
    const result = await GET("admin/warranty-alert-settings");
    loading(false);
    const item = result?.status && result.data ? result.data : null;
    const form = item ? toForm(item) : null;
    this.setState({ item, loaded: true, form, base_json: form ? JSON.stringify(form) : "", input: { to: "", cc: "" }, input_error: { to: "", cc: "" }, focus_list: "" });
  };

  isDirty = () => !!this.state.form && JSON.stringify(this.state.form) !== this.state.base_json;

  Discard = () => {
    const { item } = this.state;
    if (!item) return;
    const form = toForm(item);
    this.setState({ form, base_json: JSON.stringify(form), input: { to: "", cc: "" }, input_error: { to: "", cc: "" } });
  };

  SetForm = (patch) => this.setState({ form: { ...this.state.form, ...patch } });

  ToggleBucket = (value) => {
    const { buckets } = this.state.form;
    const next = buckets.includes(value) ? buckets.filter((b) => b !== value) : [...buckets, value];
    this.SetForm({ buckets: THRESHOLDS.filter((b) => next.includes(b)) });
  };

  SetTime = (part, value) => {
    const [h, m] = this.state.form.send_time.split(":");
    this.SetForm({ send_time: part === "h" ? `${value}:${m}` : `${h}:${value}` });
  };

  // ---------------------------------------------------------------- ผู้รับ
  SetInput = (list, value) => this.setState({ input: { ...this.state.input, [list]: value }, input_error: { ...this.state.input_error, [list]: "" }, focus_list: list });

  SetError = (list, message) => this.setState({ input_error: { ...this.state.input_error, [list]: message } });

  Suggestions = (list) => {
    const { users, input, form } = this.state;
    const keyword = input[list].trim().toLowerCase();
    if (!keyword || keyword.includes("@")) return [];
    const taken = new Set(form.recipients.filter((r) => r.type === list && r.user_uid).map((r) => r.user_uid));
    return users
      .filter((u) => u.is_active !== false && !taken.has(u.uid) && `${u.full_name || ""} ${u.email || ""}`.toLowerCase().includes(keyword))
      .slice(0, MAX_SUGGEST);
  };

  AddRecipient = (list, recipient) => {
    this.SetForm({ recipients: [...this.state.form.recipients, { type: list, is_active: true, user_uid: null, name: "", email: "", ...recipient }] });
    this.setState({ input: { ...this.state.input, [list]: "" }, input_error: { ...this.state.input_error, [list]: "" } });
  };

  AddUser = (list, user) => this.AddRecipient(list, { kind: "user", user_uid: user.uid, name: user.full_name || user.email || "", email: user.email || "" });

  OnRecipientKey = (list, e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const value = this.state.input[list].trim();
    if (!value) return;
    if (value.includes("@")) {
      if (!EMAIL_RE.test(value)) return this.SetError(list, t("was_bad_email"));
      const exists = this.state.form.recipients.some((r) => r.type === list && r.kind === "email" && r.email.toLowerCase() === value.toLowerCase());
      if (exists) return this.SetError(list, t("was_dup_recipient"));
      return this.AddRecipient(list, { kind: "email", email: value });
    }
    const [first] = this.Suggestions(list);
    if (!first) return this.SetError(list, t("was_user_not_found"));
    return this.AddUser(list, first);
  };

  RemoveRecipient = (target) => this.SetForm({ recipients: this.state.form.recipients.filter((r) => r !== target) });

  // ---------------------------------------------------------------- บันทึก / ทดสอบ
  Validate = () => {
    const { form } = this.state;
    if (!form.is_enabled) return "";
    if (!form.recipients.some((r) => r.type === "to")) return t("was_need_to");
    if (!form.buckets.length) return t("was_need_bucket");
    return "";
  };

  Save = async () => {
    const problem = this.Validate();
    if (problem) {
      alert("", "warning", t("warning"), problem);
      return;
    }
    const { form } = this.state;
    this.setState({ saving: true });
    loading(true);
    const result = await PUT("admin/warranty-alert-settings", {
      is_enabled: form.is_enabled,
      buckets: form.buckets,
      frequency: form.frequency,
      weekly_day: form.frequency === "weekly" ? form.weekly_day : undefined,
      send_time: form.send_time,
      urgent_immediate: form.urgent_immediate,
      include_owner_sales: form.include_owner_sales,
      attach_excel: form.attach_excel,
      recipients: form.recipients.map((r) => (r.kind === "user" ? { type: r.type, user_uid: r.user_uid } : { type: r.type, email: r.email })),
    });
    loading(false);
    this.setState({ saving: false });
    if (!result?.status) return;
    alert("", "success", t("success"), t("was_saved"));
    await this.Load();
  };

  // ดูตัวอย่างอีเมล (ไม่ส่ง ไม่บันทึก)
  OpenPreview = async () => {
    this.setState({ preview: { loading: true, data: null, error: false } });
    const seq = (this.previewSeq = (this.previewSeq || 0) + 1);
    const result = await GET("admin/warranty-alert-settings/preview-email");
    if (seq !== this.previewSeq || !this.state.preview) return;
    const ok = !!result?.status && !!result.data;
    this.setState({ preview: { loading: false, data: ok ? result.data : null, error: !ok } });
  };

  TestSend = async () => {
    loading(true);
    const result = await POST("admin/warranty-alert-settings/test-send", {});
    loading(false);
    if (!result?.status) return;
    const d = result.data || {};
    alert("", "success", t("success"), fill(t("was_test_sent"), { email: d.sent_to || "-", rows: d.row_count ?? 0, new: d.new_count ?? 0 }));
  };

  // ---------------------------------------------------------------- render
  renderPreview() {
    const { preview } = this.state;
    if (!preview) return null;
    const d = preview.data;
    return (
      <Modal show onHide={() => { this.previewSeq = (this.previewSeq || 0) + 1; this.setState({ preview: null }); }} centered size="xl" scrollable contentClassName="border-0 rounded-4 shadow-lg" data-testid="was-preview">
        <Modal.Header className="border-bottom border-light px-4 py-3 bg-white">
          <Modal.Title className="fw-bold text-dark text-18">{t("was_pv_title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4" style={{ backgroundColor: "#F4F7FE" }}>
          <div className="text-secondary text-12 mb-3">{t("was_pv_note")}</div>
          {preview.loading ? <div className="text-center text-secondary py-5">{t("was_pv_loading")}</div> : null}
          {!preview.loading && preview.error ? <div className="alert alert-danger py-2 text-14">{t("was_pv_fail")}</div> : null}
          {!preview.loading && d ? (
            <>
              <div className="bg-white rounded-3 border p-3 mb-3 text-14" data-testid="was-pv-subject">
                <div><b>{t("was_pv_subject")}:</b> {d.subject}</div>
                <div className="text-secondary text-12 mt-1">{fill(t("was_pv_rows"), { rows: d.row_count ?? 0, new: d.new_count ?? 0 })}</div>
              </div>
              <iframe title="email-preview" sandbox="" srcDoc={d.html || ""} className="w-100 bg-white border rounded-3" style={{ height: "62vh" }} data-testid="was-pv-frame" />
            </>
          ) : null}
        </Modal.Body>
      </Modal>
    );
  }

  renderRecipients(list) {
    const { form, input, input_error, focus_list } = this.state;
    const chips = form.recipients.filter((r) => r.type === list);
    const suggestions = focus_list === list ? this.Suggestions(list) : [];
    return (
      <div className="mb-2">
        <label className="form-label text-12 text-secondary mb-1 w-100">
          {t(`was_${list}`)}
          <div className={`wx2-recip ${input_error[list] ? "bad" : ""}`} data-testid={`was-recip-${list}`}>
            {chips.map((r, i) => (
              <span key={`${r.kind}-${r.user_uid || r.email}-${i}`} className={`r ${r.kind === "user" ? "user" : ""} ${r.is_active ? "" : "inactive"}`} title={r.is_active ? "" : t("was_inactive_tip")}>
                {r.kind === "user" ? "👤 " : "✉️ "}
                {r.kind === "user" ? r.name || r.email : r.email}
                {r.is_active ? null : <span className="text-12 fw-semibold text-secondary bg-light px-2 py-1 rounded-pill" style={{ textDecoration: "none" }}>{t("was_inactive")}</span>}
                <button type="button" aria-label="remove" onClick={() => this.RemoveRecipient(r)}>×</button>
              </span>
            ))}
            <input
              value={input[list]}
              placeholder={t("was_recipient_ph")}
              onChange={(e) => this.SetInput(list, e.target.value)}
              onKeyDown={(e) => this.OnRecipientKey(list, e)}
              onFocus={() => this.setState({ focus_list: list })}
              onBlur={() => this.setState({ focus_list: "" })}
            />
            {suggestions.length ? (
              <div className="wx2-suggest">
                {suggestions.map((u) => (
                  <button type="button" key={u.uid} onMouseDown={(e) => { e.preventDefault(); this.AddUser(list, u); }}>
                    👤 {u.full_name} <span className="text-secondary text-12">{u.email}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </label>
        {input_error[list] ? <div className="text-danger text-12" data-testid={`was-recip-error-${list}`}>{input_error[list]}</div> : null}
      </div>
    );
  }

  renderSwitch(checked, onChange, label, testid) {
    return (
      <div className="form-check form-switch mb-2">
        <label className="form-check-label text-14 text-dark">
          <input className="form-check-input" type="checkbox" role="switch" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
          {label}
        </label>
      </div>
    );
  }

  renderForm() {
    const { form, item, saving } = this.state;
    if (!form || !item) return null;
    const dirty = this.isDirty();
    const status = formStatus(form);
    const [hour, minute] = form.send_time.split(":");
    const minutes = MINUTES.includes(minute) ? MINUTES : [...MINUTES, minute].sort();
    const disabledStyle = form.is_enabled ? undefined : { opacity: 0.45, pointerEvents: "none" };

    return (
      <div className="bg-white rounded-4 shadow-sm p-4" data-testid="was-form">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
          <div>
            <span className="text-12 fw-bold px-2 py-1 rounded-pill bg-light" style={{ color: STATUS_COLORS[status] }}>● {t(`was_st_${status}`)}</span>
            <div className="text-secondary text-12 mt-1">
              {item.configured && item.updated_at
                ? fill(t("was_updated"), { at: fmtDateTime(item.updated_at), by: item.updated_by_name || "-" })
                : t("was_never")}
            </div>
          </div>
          {this.renderSwitch(form.is_enabled, (v) => this.SetForm({ is_enabled: v }), t("was_enable"), "was-enable")}
        </div>

        {form.is_enabled && status === "no_recipient" ? <div className="alert alert-danger py-2 text-14 fw-semibold" data-testid="was-no-recipient">{t("was_no_to_warn")}</div> : null}
        {(item.warnings || []).map((w, i) => <div className="alert alert-warning py-2 text-14 fw-semibold" key={`${w.code}-${i}`}>{w.message}</div>)}

        <div style={disabledStyle}>
          <div className="text-14 fw-bold text-dark mb-2">{t("was_s1")}</div>
          <div className="d-flex flex-wrap gap-2 mb-4" data-testid="was-thresholds">
            {THRESHOLDS.map((b) => (
              <button type="button" key={b} className={`btn btn-sm ${form.buckets.includes(b) ? "btn-primary" : "btn-white border"} rounded-3 fw-semibold`} onClick={() => this.ToggleBucket(b)} data-testid={`was-thr-${b}`}>
                {fill(t("was_days"), { n: b })}
              </button>
            ))}
          </div>

          <div className="text-14 fw-bold text-dark mb-2">{t("was_s2")}</div>
          {this.renderRecipients("to")}
          {this.renderRecipients("cc")}
          <div className="text-secondary text-12 mb-2">{t("was_recipient_hint")}</div>
          <div className="mb-4">{this.renderSwitch(form.include_owner_sales, (v) => this.SetForm({ include_owner_sales: v }), t("was_sales"), "was-sales")}</div>

          <div className="text-14 fw-bold text-dark mb-2">{t("was_s3")}</div>
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <label className="form-label text-12 text-secondary mb-1">{t("was_freq")}</label>
              <select className="form-select" value={form.frequency} onChange={(e) => this.SetForm({ frequency: e.target.value })} data-testid="was-freq">
                <option value="daily">{t("was_freq_daily")}</option>
                <option value="weekly">{t("was_freq_weekly")}</option>
              </select>
            </div>
            {form.frequency === "weekly" ? (
              <div className="col-md-4">
                <label className="form-label text-12 text-secondary mb-1">{t("was_day")}</label>
                <select className="form-select" value={form.weekly_day} onChange={(e) => this.SetForm({ weekly_day: Number(e.target.value) })}>
                  {WEEK_DAYS.map((d) => <option key={d} value={d}>{t(`was_day_${d}`)}</option>)}
                </select>
              </div>
            ) : null}
            <div className="col-md-4">
              <label className="form-label text-12 text-secondary mb-1">{t("was_time")}</label>
              <div className="d-flex align-items-center gap-1">
                <select className="form-select" value={hour} onChange={(e) => this.SetTime("h", e.target.value)} aria-label="hour">
                  {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
                :
                <select className="form-select" value={minute} onChange={(e) => this.SetTime("m", e.target.value)} aria-label="minute">
                  {minutes.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
          </div>
          {this.renderSwitch(form.urgent_immediate, (v) => this.SetForm({ urgent_immediate: v }), t("was_urgent"), "was-urgent")}
          {this.renderSwitch(form.attach_excel, (v) => this.SetForm({ attach_excel: v }), t("was_excel"), "was-excel")}
        </div>

        <div className="d-flex justify-content-between flex-wrap gap-2 pt-3 mt-3 border-top border-light">
          <div className="d-flex gap-2 flex-wrap">
            <button type="button" className="btn btn-white border px-3 py-2 fw-bold rounded-3" onClick={this.OpenPreview} data-testid="was-preview-btn">
              👁 {t("was_preview")}
            </button>
            {can("warranty_alert.update") && (
              <button type="button" className="btn btn-white border px-3 py-2 fw-bold rounded-3 warranty_alert.update" onClick={this.TestSend} disabled={dirty || !item.configured} title={dirty || !item.configured ? t("was_test_hint") : ""} data-testid="was-test-send">
                ✉️ {t("was_test")}
              </button>
            )}
          </div>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-white border px-3 py-2 fw-bold rounded-3" onClick={this.Discard} disabled={!dirty}>{t("was_reset")}</button>
            {can("warranty_alert.update") && (
              <button type="button" className="btn btn-primary px-4 py-2 fw-bold rounded-3 warranty_alert.update" onClick={this.Save} disabled={saving || (!dirty && item.configured)} data-testid="was-save">{t("save")}</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { loaded, item } = this.state;
    return (
      <div className="w-100 min-vh-100" style={{ backgroundColor: "#F4F7FE" }}>
        <NavbarAdmin />
        <div className="body p-4 mt-3">
          <div className="mb-4">
            <h3 className="fw-bold mb-1 text-dark">{t("was_title")}</h3>
            <div className="text-secondary text-14">{t("was_desc")}</div>
          </div>
          {loaded && !item ? <div className="bg-white rounded-4 shadow-sm p-5 text-center text-secondary">{t("was_load_failed")}</div> : null}
          {item ? <div className="row g-3"><div className="col-lg-9">{this.renderForm()}</div></div> : null}
        </div>
        {this.renderPreview()}
      </div>
    );
  }
}
